/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { LevelValue } from 'libs/end-to-end/src/progress';
import { ProgressPresentationData } from 'libs/end-to-end/src/progress';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface ProgressPageChartTooltipPropsPure {
  displayPeriod: string;
  displayCount: string;
  displayNumerator: string | undefined;
  compareDisplayCount: string | undefined;
  compareDisplayNumerator: string | undefined;
  metricDisplayName: string;
  whiteLabelingSubjectPlural: string;
  // This property is not pure, but its complicated to determine based on call site.
  isMetricBoolean: boolean;
  displayValue: string;
  displayLevel: string;
  compareDisplayPeriod: string | undefined;
  compareDisplayValue: string | undefined;
  deltaDisplayValue: string | undefined;
  color: Color;
  // Whether or not to hide the background for nesting inside a pre-existing container.
  inlineMode?: boolean;
}

export const ProgressPageChartTooltipPure: React.FC<
  ProgressPageChartTooltipPropsPure
> = ({
  deltaDisplayValue,
  compareDisplayPeriod,
  compareDisplayValue,
  color,
  displayValue,
  metricDisplayName,
  isMetricBoolean,
  whiteLabelingSubjectPlural,
  displayPeriod,
  displayLevel,
  displayCount,
  displayNumerator,
  compareDisplayCount,
  compareDisplayNumerator,
  inlineMode,
}) => {
  const language = useLanguage();
  const finalMetricDisplay = isMetricBoolean
    ? metricDisplayName
    : `Average ${metricDisplayName}`;

  const children = useMemo(() => {
    return (
      <Spacing size={'md'} direction={'vertical'}>
        <Spacing size={'sm'} direction={'horizontal'} className="items-center">
          <div
            className={`h-[10px] w-[10px] ${ConfigColors[color].Primary.bg}`}
          />
          <S2Typography
            variant={'headline-6-alt'}
            translatedContent={displayLevel}
            textColorVariant={'gray-900'}
          />

          {deltaDisplayValue && (
            <Spacing size={'sm'} direction={'horizontal'}>
              <S2Typography
                variant={'small-label-alt'}
                translatedContent={deltaDisplayValue}
                textColorVariant={'gray-900'}
              />

              <S2Typography
                variant={'small-label-alt'}
                translatedContent={`vs. previous period`}
                textColorVariant={'gray-900'}
              />
            </Spacing>
          )}
        </Spacing>

        <Spacing size="sm" direction="vertical">
          <S2Typography
            variant={'headline-6-alt'}
            translatedContent={`${displayPeriod}:`}
            textColorVariant={'gray-900'}
          />

          <Spacing size="xs" direction="vertical">
            <Spacing size={'sm'} direction={'horizontal'}>
              <S2Typography
                variant={'body-6'}
                translatedContent={displayValue}
                textColorVariant={'gray-900'}
              />
              <S2Typography
                variant={'body-6'}
                translatedContent={finalMetricDisplay}
                textColorVariant={'gray-600'}
              />
            </Spacing>

            <Spacing size={'sm'} direction={'horizontal'}>
              {displayNumerator && isMetricBoolean ? (
                <S2Typography
                  variant={'body-6'}
                  translatedContent={language.subjectsCount({
                    subjectsCount: displayNumerator,
                    totalSample: displayCount,
                    whiteLabelingSubjectPlural,
                  })}
                  textColorVariant={'gray-900'}
                />
              ) : (
                <>
                  <S2Typography
                    variant={'body-6'}
                    translatedContent={displayCount}
                    textColorVariant={'gray-900'}
                  />
                  <S2Typography
                    variant={'body-6'}
                    translatedContent={whiteLabelingSubjectPlural}
                    textColorVariant={'gray-600'}
                  />
                </>
              )}
            </Spacing>
          </Spacing>
        </Spacing>

        {compareDisplayPeriod && compareDisplayValue && compareDisplayCount && (
          <Spacing size={'sm'} direction={'vertical'}>
            <S2Typography
              variant={'headline-6-alt'}
              translatedContent={`Compared to ${compareDisplayPeriod}:`}
              textColorVariant={'gray-900'}
            />

            <Spacing size={'sm'} direction={'horizontal'}>
              <S2Typography
                variant={'body-6'}
                translatedContent={compareDisplayValue}
                textColorVariant={'gray-900'}
              />
              <S2Typography
                variant={'body-6'}
                translatedContent={finalMetricDisplay}
                textColorVariant={'gray-600'}
              />
            </Spacing>
            <Spacing size={'sm'} direction={'horizontal'}>
              {compareDisplayNumerator ? (
                <S2Typography
                  variant={'body-6'}
                  translatedContent={language.subjectsCount({
                    subjectsCount: compareDisplayNumerator,
                    totalSample: compareDisplayCount,
                    whiteLabelingSubjectPlural,
                  })}
                  textColorVariant={'gray-900'}
                />
              ) : (
                <>
                  <S2Typography
                    variant={'body-6'}
                    translatedContent={compareDisplayCount}
                    textColorVariant={'gray-900'}
                  />
                  <S2Typography
                    variant={'body-6'}
                    translatedContent={whiteLabelingSubjectPlural}
                    textColorVariant={'gray-600'}
                  />
                </>
              )}
            </Spacing>
          </Spacing>
        )}
      </Spacing>
    );
  }, [
    color,
    compareDisplayCount,
    compareDisplayNumerator,
    compareDisplayPeriod,
    compareDisplayValue,
    deltaDisplayValue,
    displayCount,
    displayLevel,
    displayNumerator,
    displayPeriod,
    displayValue,
    finalMetricDisplay,
    isMetricBoolean,
    language,
    whiteLabelingSubjectPlural,
  ]);
  return inlineMode ? (
    children
  ) : (
    <Card as="div" paddingClassName="p-2" className="whitespace-nowrap">
      {children}
    </Card>
  );
};

export interface ProgressPageChartTooltipProps {
  groupId: string;
  value: LevelValue;
  currentMetricDefinition: MyMetricDefinition | null;
  filteredData: ProgressPresentationData;
  metricUnits: NumericDisplayUnit | null;
  inlineMode?: boolean;
}

export const ProgressPageChartTooltip: React.FC<
  ProgressPageChartTooltipProps
> = ({
  groupId,
  value,
  currentMetricDefinition,
  filteredData,
  metricUnits,
  inlineMode,
}) => {
  if (!currentMetricDefinition || !metricUnits) {
    return <></>;
  }

  const primaryData =
    filteredData.primaryBucketToComparisonBucketData[
      `${groupId}_${value.level}`
    ];

  let displayDelta: string | undefined;
  let compareDisplayCount: string | undefined;
  let compareDisplayPeriod: string | undefined;
  let compareDisplayValue: string | undefined;

  if (primaryData) {
    const { primary, comparison, groupComparison } = primaryData;
    const primaryMetricValue = primary.value || 0;
    const comparisonMetricValue = comparison.value || 0;
    const changedMetricValue = primaryMetricValue - comparisonMetricValue;
    const formattedChangedMetricValue = formatNumberForDisplayInUnits({
      value: changedMetricValue,
      units: metricUnits,
      isPercentageAlreadyMultipliedBy100: false,
    });
    compareDisplayValue = formatNumberForDisplayInUnits({
      value: comparisonMetricValue,
      units: metricUnits,
      isPercentageAlreadyMultipliedBy100: false,
    });

    displayDelta =
      changedMetricValue >= 0
        ? `+${formattedChangedMetricValue}`
        : `${formattedChangedMetricValue}`;

    compareDisplayCount = String(comparison.count);
    compareDisplayPeriod = groupComparison;
  }

  const valueDataToDisplay = primaryData?.primary ?? value;
  if (valueDataToDisplay.value === null) {
    return <></>;
  }

  const formattedValueDataToDisplay = formatNumberForDisplayInUnits({
    value: valueDataToDisplay.value,
    units: metricUnits,
    isPercentageAlreadyMultipliedBy100: false,
  });

  return (
    <ProgressPageChartTooltipPure
      displayPeriod={groupId}
      displayCount={String(valueDataToDisplay.count)}
      displayNumerator={String(valueDataToDisplay.numerator)}
      compareDisplayCount={compareDisplayCount}
      compareDisplayNumerator={String(valueDataToDisplay.numerator)}
      metricDisplayName={currentMetricDefinition.displayName}
      whiteLabelingSubjectPlural={
        currentMetricDefinition.whiteLabeling.subjectPlural
      }
      isMetricBoolean={filteredData.isMetricBoolean}
      displayValue={formattedValueDataToDisplay}
      displayLevel={valueDataToDisplay.level}
      compareDisplayPeriod={compareDisplayPeriod}
      compareDisplayValue={compareDisplayValue}
      deltaDisplayValue={displayDelta}
      color={valueDataToDisplay.color}
      inlineMode={inlineMode}
    />
  );
};
